import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { FileSignature, Loader2 } from 'lucide-react';
import { acceptTrainerAgreement } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TrainerAgreementModal = ({ open, onAccept, onClose }) => {
  const { refreshUser } = useAuth();
  const scrollRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setScrolledToEnd(false);
      setAgreed(false);
      setTimeout(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight <= el.clientHeight + 4) setScrolledToEnd(true);
      }, 50);
    }
  }, [open]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setScrolledToEnd(true);
  };

  const handleAccept = useCallback(async () => {
    if (!scrolledToEnd || !agreed) return;
    setSubmitting(true);
    try {
      await acceptTrainerAgreement();
      await refreshUser();
      toast.success('Agreement accepted, your account is active');
      onAccept?.();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Could not record acceptance';
      toast.error(typeof msg === 'string' ? msg : 'Could not record acceptance');
    } finally {
      setSubmitting(false);
    }
  }, [scrolledToEnd, agreed, onAccept, refreshUser]);

  const canSubmit = scrolledToEnd && agreed && !submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="bg-[#0d0d10] border-zinc-800 max-w-2xl text-white"
        data-testid="trainer-agreement-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#C0A062]/10 border border-[#C0A062]/30 flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-[#C0A062]" />
            </div>
            <DialogTitle className="text-white text-xl">Independent Contractor Agreement</DialogTitle>
          </div>
          <DialogDescription className="text-zinc-400 text-sm">
            Before your trainer account is fully activated, please review and accept this agreement.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="max-h-[50vh] overflow-y-auto pr-4 -mr-4 text-sm text-zinc-300 leading-relaxed space-y-4 border border-zinc-800 rounded-md p-4 bg-[#09090B]"
          data-testid="agreement-scroll"
        >
          <p className="font-semibold text-white">1. Independent Contractor Status</p>
          <p>
            I confirm and acknowledge that I am acting as an <strong>independent contractor</strong>, and
            <strong> not</strong> an employee, agent, partner, or joint venturer of DojoDash. Nothing in
            this agreement or in my use of the platform creates an employment relationship. I set my own
            hours, my own rates, my own service methods, and I bear my own business risk.
          </p>

          <p className="font-semibold text-white">2. Taxes</p>
          <p>
            I am <strong>solely responsible</strong> for the calculation, withholding, and timely payment
            of all federal, state, and local taxes on income I earn through DojoDash, including
            self-employment tax. DojoDash will not withhold taxes on my behalf and will issue tax forms
            (e.g., 1099-K) only as required by law.
          </p>

          <p className="font-semibold text-white">3. Professional Liability Insurance</p>
          <p>
            I agree to maintain, at my own expense, professional liability insurance with a per-occurrence
            limit of at least <strong>$1,000,000 USD</strong>, valid for the categories of training I offer
            through the platform. I will provide proof of insurance to DojoDash upon written request.
            Operating without compliant coverage is a material breach of this agreement.
          </p>

          <p className="font-semibold text-white">4. Non-Solicitation</p>
          <p>
            For a period of <strong>twelve (12) months</strong> following termination of my account
            (whether voluntary or involuntary), I agree not to directly or indirectly solicit, contact, or
            accept payment from any client I met or was connected with through DojoDash for the purpose
            of providing training services outside the DojoDash platform.
          </p>

          <p className="font-semibold text-white">5. Conduct, Safety, and Quality</p>
          <p>
            I am solely responsible for: (a) my conduct before, during, and after every session; (b) the
            safety practices I use; (c) the quality and accuracy of the services I provide; and (d)
            holding all certifications and licenses required by my jurisdiction. I will not engage in any
            harassing, abusive, discriminatory, or illegal conduct on or off the platform with respect to
            any DojoDash user.
          </p>

          <p className="font-semibold text-white">6. Verification &amp; Background</p>
          <p>
            I authorize DojoDash to verify the identifying information I provide and, where permitted by
            law, to run periodic background checks. I agree to cooperate with any reasonable verification
            request. Failure to do so may result in suspension or termination of my account.
          </p>

          <p className="font-semibold text-white">7. Payments &amp; Fees</p>
          <p>
            DojoDash processes client payments through Stripe and pays out <strong>80%</strong> of each
            session price to me via Stripe Connect. DojoDash retains a <strong>20% platform fee</strong>.
            I will not collect or accept off-platform payment for any DojoDash-booked session.
          </p>

          <p className="font-semibold text-white">8. Termination</p>
          <p>
            <strong>DojoDash may suspend or terminate my account at any time, with or without cause and
            with or without notice.</strong> Upon termination, I will complete any already-booked sessions
            in good faith (or refund clients), and the non-solicitation clause above remains in force for
            12 months.
          </p>

          <p className="font-semibold text-white">9. Indemnification</p>
          <p>
            I agree to indemnify and hold harmless DojoDash, its founders, officers, employees, and
            agents from any claim arising out of my conduct, my services, my representations, or my
            breach of this agreement.
          </p>

          <p className="font-semibold text-white">10. Governing Law and Arbitration</p>
          <p>
            This agreement is governed by the laws of the State of Florida. Any dispute will be resolved
            by binding individual arbitration in Miami-Dade County, Florida. I waive any right to a jury
            trial and to participate in any class or collective proceeding.
          </p>

          <p className="text-zinc-500 text-xs pt-2">,  End of agreement , </p>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <Checkbox
            id="agreement-agree"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            disabled={!scrolledToEnd}
            data-testid="agreement-agree-checkbox"
            className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#C0A062] data-[state=checked]:border-[#C0A062]"
          />
          <label htmlFor="agreement-agree" className={`text-sm leading-relaxed ${scrolledToEnd ? 'text-zinc-200' : 'text-zinc-500'}`}>
            I confirm I am an independent contractor, will pay my own taxes, will carry $1M+ professional
            liability insurance, and agree to the non-solicitation, termination, and other terms above.
          </label>
        </div>

        {!scrolledToEnd && (
          <p className="text-xs text-amber-400 mt-2" data-testid="agreement-scroll-hint">
            Scroll to the bottom to enable the checkbox.
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          {onClose && (
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
              data-testid="agreement-cancel-btn"
            >
              Later
            </Button>
          )}
          <Button
            onClick={handleAccept}
            disabled={!canSubmit}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold disabled:opacity-40"
            data-testid="agreement-accept-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            I Accept &amp; Activate My Account
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrainerAgreementModal;
